import { BaseWidgetContainer } from 'components/page/BaseLandingPage';
import { SortBar } from 'shared/sort_bar/SortBar';
import styled, { css } from 'styled-components';
import { tokens } from 'styles/tokens';

// Grid template column values
export const gridColumnSizes = {
    issueNumber: '1fr',
    severity: '1.2fr',
    priority: '1.2fr',
    status: '0.8fr',
    faultType: '1.1fr',
    faultLocation: '1.5fr',
    created: '1.6fr',
    lastUpdated: '2fr',
    actions: '0.3fr',
} as const;

// Grid template columns string
export const gridTemplateColumnsString = `${gridColumnSizes.issueNumber} ${gridColumnSizes.severity} ${gridColumnSizes.priority} ${gridColumnSizes.status} ${gridColumnSizes.faultType} ${gridColumnSizes.faultLocation} ${gridColumnSizes.created} ${gridColumnSizes.lastUpdated} ${gridColumnSizes.actions}`;

const gridStyles = css`
    display: grid;
    grid-template-columns: ${gridTemplateColumnsString};
    justify-content: space-between;
    align-items: center;
    padding: 0 1.6rem;
    gap: 0.8rem;

    @media (max-width: 760px) {
        grid-template-columns: ${gridColumnSizes.issueNumber} ${gridColumnSizes.severity} ${gridColumnSizes.status} ${gridColumnSizes.actions};
        gap: 0.8rem;
        padding: 0 1rem;
    }
`;

// Widget Container
export const WidgetContainer = styled(BaseWidgetContainer)`
    gap: 1.2rem;
    flex: 1 1 auto;
    min-height: 0;
`;

// Header Section
export const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    @media (max-width: 600px) {
        width: 100%;
        overflow-x: auto;
        padding-bottom: 0.8rem;
    }
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
`;

export const Title = styled.h2`
    font-weight: 500;
    font-size: 2.4rem;
    line-height: 1.25;
    color: ${tokens.text.primary};
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
`;

export const FiltersContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    flex-wrap: wrap;
`;

export const VerticalDivider = styled.div`
    width: 0.1rem;
    height: 2.4rem;
    background-color: ${tokens.divider.default};
    margin: 0 0.8rem;
`;

// Links
export const DeskLink = styled.a`
    color: ${tokens.text.link};
    text-decoration: none;
    font-size: 1.4rem;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    white-space: nowrap;

    svg {
        font-size: 1.4rem;
        font-weight: 900;
    }

    &:hover {
        color: ${tokens.text.linkHover};
        text-decoration: underline;
    }
`;

// Table Components
export const IssuesTable = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    border-radius: 0.8rem;
    overflow: hidden;
    background: ${tokens.surface.raised};
    min-height: 0;
`;

export const IssuesSortBar = styled(SortBar)`
    ${gridStyles}
    background-color: ${tokens.background.subtle};
    border-radius: 0.8rem;
    flex-shrink: 0;
`;

export const TableContent = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    margin-top: 0.8rem;
    overflow-y: auto;
    min-height: 0;
`;

// Base Cell styles
const BaseCell = styled.div`
    font-size: 1.4rem;
    color: ${tokens.text.primary};
    display: flex;
    align-items: center;
    min-width: 0;
    padding: 0;
    overflow: hidden;
    text-overflow: ellipsis;
`;

// Shared truncation styles for multi-line text
const multiLineTruncation = css`
    width: 100%;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 2rem;
    white-space: normal;
    word-break: break-word;
`;

export const IssueNumber = styled(BaseCell)`
    text-decoration: underline dotted;
    text-underline-offset: 0.2rem;
    min-width: max-content;
    white-space: nowrap;
    gap: 0.8rem;
`;

export const Severity = styled(BaseCell)`
    padding-left: 0.5rem;
    min-width: max-content;
    padding-right: 0.8rem;
`;

export const Priority = styled(BaseCell)`
    padding-left: 0.5rem;
    min-width: max-content;
    padding-right: 0.8rem;
`;

export const Status = styled(BaseCell)`
    padding-left: 0.5rem;
    min-width: max-content;
    padding-right: 0.8rem;
`;

export const FaultType = styled(BaseCell)`
    overflow: visible;
    padding-left: 0.8rem;
    padding-right: 0.8rem;
    white-space: normal;

    & > div {
        ${multiLineTruncation}
    }

    & > div:not(.no-value) {
        text-decoration: underline dotted;
        text-underline-offset: 0.2rem;
    }
`;

export const FaultLocation = styled(BaseCell)`
    overflow: visible;
    padding-right: 1.2rem;
    padding-left: 0.8rem;
    white-space: normal;

    & > span {
        ${multiLineTruncation}
    }

    @media (max-width: 1520px) and (min-width: 1025px) {
        padding-left: 0.4rem;
        padding-right: 0.4rem;
    }

    @media (max-width: 1024px) {
        padding-left: 0;
    }
`;

export const DateCreated = styled(BaseCell)`
    display: flex;
    align-items: center;
    min-width: 12rem;
    padding-left: 0.5rem;
    min-width: max-content;

    .flex-col {
        width: 100%;
        font-size: 1.4rem;
        font-style: normal;
        font-weight: 300;
        line-height: 2rem;

        time {
            font-weight: 300;
        }

        /* Target DateTimeDisplay wrapper */
        & > div {
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
        }
    }
`;

export const LastUpdated = styled(BaseCell)`
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding-left: 0.5rem;
    padding-right: 0.8rem;
    position: relative;
    min-width: max-content;

    .flex-col {
        min-width: 9.5rem;
        max-width: 9.5rem;
        font-size: 1.4rem;
        font-style: normal;
        font-weight: 300;
        line-height: 2rem;

        time {
            font-weight: 300;
        }

        /* Target DateTimeDisplay wrapper */
        & > div {
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
        }
    }

    @media (max-width: 1052px) and (min-width: 1024px),
        (max-width: 1023px) and (min-width: 921px),
        (max-width: 920px) and (min-width: 788px) {
        padding-right: 4rem;
    }
`;

// Update TableRow to use an anchor tag
export const TableRow = styled.a`
    display: grid;
    grid-template-columns: ${gridTemplateColumnsString};
    padding: 0.8rem 1.6rem;
    text-decoration: none;
    color: inherit;
    position: relative;
    align-items: center;

    /* Ensure all direct children are vertically centered */
    & > * {
        display: flex;
        align-items: center;
    }

    &:hover {
        background-color: ${tokens.action.hover};
    }

    &:not(:last-child) {
        border-bottom: 0.1rem solid ${tokens.divider.default};
    }
`;

export const Actions = styled(BaseCell)`
    justify-content: center;
    width: 3.2rem;
    padding: 0;
    flex-shrink: 0;
    margin-left: auto;

    svg {
        font-size: 1.4rem;
        font-weight: 900;
        color: ${tokens.text.primary};
        opacity: 0;
        transition: opacity 0.2s ease;
    }

    ${TableRow}:hover & svg {
        opacity: 1;
    }

    @media (max-width: 1024px) {
        display: none;
    }
`;

export const EmptyStateRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 2.4rem;
    color: ${tokens.text.secondary};
    font-size: 2rem;
    font-weight: 500;
    line-height: 1.2;

    svg {
        font-size: 4.8rem;
    }
`;

export const SortBarLayoutOverrides = css`
    padding: 0.75rem 2rem;
`;

export const SortItemOverrides = css`
    margin: 0;
    white-space: nowrap;

    /* InfoPopup styling to ensure it stays inline */
    & span {
        display: inline-flex;
        align-items: center;
    }
`;

export const PopupWrapper = styled.span`
    margin-left: 0.4rem;
`;
